import React from 'react';
import Icon from './Icon';
import Reveal from './Reveal';
import './Apps.css';

const CUSTOMER_APP = {
  id: 'customer',
  tag: 'On the App Store & Google Play',
  name: 'BikesDrop, the Customer App',
  img: '/assets/bikesdrop/schedule_delivery.jpg',
  blurb:
    'The app you open for anything you need moved, ordered or delivered. Book a pickup, request a campus or community service, move cargo, order food from over 1,000 restaurants, and shop groceries from over 1,000 stores and local markets, all with live tracking from one app.',
  cta: 'Get the Customer App',
  categories: [
    { icon: 'package', title: 'Pickup Delivery', text: 'Send a document, parcel or gift anywhere in your city, tracked live from pickup to drop off.' },
    { icon: 'mapPin', title: 'Campus & Community Services', text: 'Request an errand wherever you are, on campus or around your neighbourhood.' },
    { icon: 'truck', title: 'Cargo Delivery', text: 'Move heavy or bulky items with a real truck and trained handlers.' },
    { icon: 'zap', title: 'Okada', text: 'Hop on the back of a verified rider’s motorbike and beat the traffic across town.' },
    { icon: 'shoppingBag', title: 'Food, 1,000+ Restaurants', text: 'Ghanaian classics and continental favourites through BikesFood.' },
    { icon: 'cart', title: 'Groceries, 1,000+ Stores', text: 'Fresh produce, pantry staples and more through BikesGroceries.' },
  ],
  chipGroups: [
    {
      label: 'Campus & community services',
      items: ['Grocery & market runs', 'Gas cylinder refills', 'Printing & photocopying', 'Food pickup', 'Pharmacy runs', 'Document drop off', 'Waiting-in-line errands'],
    },
    {
      label: 'Campuses we reach',
      items: ['University of Ghana, Legon', 'KNUST, Kumasi', 'University of Cape Coast', 'Ashesi University', 'GIMPA', 'UEW, Winneba', 'Central University', 'Accra Technical University'],
    },
    {
      label: 'Popular communities',
      items: ['East Legon', 'Osu', 'Spintex', 'Tema', 'Madina', 'Adenta', 'Dansoman', 'Kaneshie', 'Ashaiman', 'Kasoa', 'Achimota', 'Circle'],
    },
    {
      label: 'Cargo delivery covers',
      items: ['Home & office moving', 'Furniture & appliances', 'Store restocking', 'Construction materials', 'Event equipment', 'Bulk business shipments'],
    },
    {
      label: 'Ghanaian favourites on BikesFood',
      items: ['Jollof rice', 'Waakye', 'Banku & tilapia', 'Fufu & light soup', 'Kenkey & fish', 'Red red', 'Tuo zaafi', 'Kelewele', 'Omo tuo'],
    },
    {
      label: 'Continental favourites on BikesFood',
      items: ['Fried rice & chicken', 'Grilled chicken & chips', 'Shawarma', 'Pizza', 'Burgers', 'Noodles', 'Pasta'],
    },
    {
      label: 'Grocery essentials on BikesGroceries',
      items: ['Rice, gari & yam', 'Fresh vegetables', 'Frozen chicken & fish', 'Smoked herrings & tilapia', 'Palm nut cream', 'Kontomire', 'Dawadawa & shrimp powder', 'Cooking oil & spices'],
    },
  ],
  requirements: [
    'A smartphone with the BikesDrop app installed',
    'A Ghana phone number to receive order and delivery updates',
    'Pickup and drop off details for whatever you need moved',
  ],
};

const BUSINESS_APP = {
  id: 'business',
  tag: 'One App, Three Ways to Work With BikesDrop',
  name: 'BikesDrop Business',
  img: '/assets/bikesdrop/pickup_delivery.jpg',
  blurb:
    'One business app for riders and truck drivers who earn by moving Ghana, merchants who list a shop or restaurant for new customers to find, and vendors who already have their own customers and just need delivery done right. Switch between rider, merchant and vendor mode without ever leaving the app.',
  cta: 'Get BikesDrop Business',
  personas: [
    {
      theme: 'teal',
      title: 'For Riders & Truck Drivers',
      subtitle: 'Earn by moving Ghana',
      features: [
        'Accept pickup, cargo and campus errand jobs from one dashboard',
        'Standby Mode: swipe through nearby jobs and accept the ones that work for you',
        'Turn-by-turn navigation with live distance and ETA to pickup and drop off',
        'Transparent per-job earnings, trip history and payout tracking',
      ],
      requirements: [
        'A valid Ghana national ID or driver’s license',
        'A roadworthy motorbike, tricycle or truck you own or ride legally',
        'A smartphone with GPS and mobile data',
      ],
    },
    {
      theme: 'amber',
      title: 'For Merchants',
      subtitle: 'Food vendors & grocery stores',
      features: [
        'List your menu on BikesFood or your products on BikesGroceries in minutes',
        'Built in Point of Sale for ringing up in person sales, even if you never list online',
        'Receive and manage orders from a live merchant dashboard',
        'Assign orders to a BikesDrop rider automatically or pick your own',
        'Create multiple staff accounts and assign each one to the store(s) they manage',
        'Sales history, payouts and performance at a glance',
      ],
      requirements: [
        'A registered business, shop, stall or verifiable trading activity',
        'A product, food or grocery catalogue you can fulfil reliably',
        'An active Ghana phone number and a fixed pickup location',
      ],
    },
    {
      theme: 'emerald',
      title: 'For Vendors',
      subtitle: 'You already sell. We just deliver.',
      features: [
        'Not listed on BikesDrop, your own customers already find you, we only handle getting it to them',
        'Book Bike Delivery at a flat rate to your own delivery zones, once BikesDrop reviews and prices them',
        'Negotiate Cargo Delivery with a rider over chat for large or bulky loads',
        'Use Pickup Delivery for a one off errand, priced by distance, no shop approval needed at all',
        'Built in Point of Sale for your own counter, completely separate from delivery',
        'Create staff accounts and choose exactly which shops and delivery types each one can book from',
        'Pay as you go per delivery, or apply for postpaid billing and settle on your own schedule',
      ],
      requirements: [
        'A business that already has its own customers, whether online, by phone or walk in',
        'A fixed pickup location for BikesDrop admin to review and price, for Bike Delivery and Cargo Delivery',
        'An active Ghana phone number',
      ],
    },
  ],
};

export default function Apps() {
  return (
    <section className="apps section" id="apps">
      <div className="container">
        <Reveal className="apps__head">
          <span className="eyebrow eyebrow--teal">Two Apps, One Platform</span>
          <h2 className="section-heading">BikesDrop for customers. BikesDrop Business for riders, merchants and vendors.</h2>
          <p className="section-sub">
            Customers order everything in one app. Riders and merchants earn in the other, vendors
            use it to book reliable delivery for a business they already run. Two dedicated apps,
            one connected network.
          </p>
        </Reveal>

        <div className="apps__list">
          {/* Customer App */}
          <Reveal className="app-panel app-panel--teal" id={CUSTOMER_APP.id}>
            <div className="app-panel__media">
              <div className="app-panel__ring">
                <img src={CUSTOMER_APP.img} alt={CUSTOMER_APP.name} />
              </div>
            </div>
            <div className="app-panel__copy">
              <span className="pill pill--teal">{CUSTOMER_APP.tag}</span>
              <h3>{CUSTOMER_APP.name}</h3>
              <p className="app-panel__blurb">{CUSTOMER_APP.blurb}</p>

              <div className="app-panel__categories">
                {CUSTOMER_APP.categories.map((c) => (
                  <div className="category-card" key={c.title}>
                    <span className="category-card__icon">
                      <Icon name={c.icon} size={19} />
                    </span>
                    <div>
                      <div className="category-card__title-row">
                        <h4>{c.title}</h4>
                        {c.comingSoon && <span className="coming-soon">Coming soon</span>}
                      </div>
                      <p>{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="app-panel__chipgroups">
                {CUSTOMER_APP.chipGroups.map((g) => (
                  <div className="chipgroup" key={g.label}>
                    <h5>{g.label}</h5>
                    <div className="chipgroup__row">
                      {g.items.map((item) => (
                        <span className="chip" key={item}>
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <h4 className="app-panel__reqheading">What you need</h4>
              <ul className="app-panel__requirements app-panel__requirements--inline">
                {CUSTOMER_APP.requirements.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>

              <a href="#download" className="btn btn--teal">
                {CUSTOMER_APP.cta}
              </a>
            </div>
          </Reveal>

          {/* Business App */}
          <Reveal className="app-panel app-panel--business app-panel--reverse" id={BUSINESS_APP.id}>
            <div className="app-panel__media">
              <div className="app-panel__ring app-panel__ring--dual">
                <img src={BUSINESS_APP.img} alt={BUSINESS_APP.name} />
              </div>
            </div>
            <div className="app-panel__copy">
              <span className="pill pill--dual">{BUSINESS_APP.tag}</span>
              <h3>{BUSINESS_APP.name}</h3>
              <p className="app-panel__blurb">{BUSINESS_APP.blurb}</p>

              <div className="personas">
                {BUSINESS_APP.personas.map((p) => (
                  <div className={`persona persona--${p.theme}`} key={p.title}>
                    <span className={`pill pill--${p.theme}`}>{p.title}</span>
                    <p className="persona__subtitle">{p.subtitle}</p>

                    <h5>What you can do</h5>
                    <ul>
                      {p.features.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>

                    <h5>What you need</h5>
                    <ul className="persona__requirements">
                      {p.requirements.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <a href="#download" className="btn btn--amber">
                {BUSINESS_APP.cta}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
